/* Mock checkout — quote + place. Place is the keystone: snapshots cart,
 * creates order, clears cart, generates NS-YYYY-NNNNN number. */

import * as store from "@/data/mocks/storage";
import * as cart from "./cart";
import { withLatency, withError } from "./_util";

function getAddressList() {
  return store.get("addresses", []) || [];
}

function findAddress(id) {
  return getAddressList().find((a) => a.id === id) || null;
}

function nextOrderNumber() {
  const year = new Date().getFullYear();
  const all = store.get("orders", []) || [];
  const sameYear = all.filter((o) => (o.number || "").includes(`-${year}-`));
  const n = sameYear.length + 1;
  return `NS-${year}-${String(n).padStart(5, "0")}`;
}

async function readCart() {
  return await cart.get();
}

export async function quote(payload = {}) {
  const cartState = await readCart();
  if (!cartState.items.length) return withError("Cart is empty", 400);

  const address = payload.address_id ? findAddress(payload.address_id) : null;
  const delivery_method = payload.delivery_method || "standard";

  // Shipping override per delivery method (M1: standard is free above ₹2k, express 150, cold 320)
  let shipping = cartState.shipping;
  if (delivery_method === "express") shipping = 150;
  else if (delivery_method === "cold_chain") shipping = 320;

  const total = Math.max(
    0,
    cartState.subtotal + cartState.gst_amount + shipping - cartState.discount
  );

  return withLatency({
    address,
    delivery_method,
    payment_method: payload.payment_method || "cod",
    items: cartState.items,
    coupon: cartState.coupon,
    subtotal: cartState.subtotal,
    gst_amount: cartState.gst_amount,
    shipping,
    discount: cartState.discount,
    total,
  });
}

export async function place(payload = {}) {
  const cartState = await readCart();
  if (!cartState.items.length) return withError("Cart is empty", 400);
  if (!payload.address_id) return withError("Address is required", 400);
  const address = findAddress(payload.address_id);
  if (!address) return withError("Address not found", 404);

  const session = store.get("session", null);
  const delivery_method = payload.delivery_method || "standard";
  let shipping = cartState.shipping;
  if (delivery_method === "express") shipping = 150;
  else if (delivery_method === "cold_chain") shipping = 320;
  const total = Math.max(
    0,
    cartState.subtotal + cartState.gst_amount + shipping - cartState.discount
  );

  const placed_at = new Date().toISOString();
  const order = {
    number: nextOrderNumber(),
    placed_at,
    user_id: session?.user_id || "anon",
    user_email: session?.email || null,
    status: "placed",
    items: cartState.items.map((it) => ({
      product_slug: it.product_slug,
      product_name: it.product_name,
      product_brand: it.product_brand,
      product_image_key: it.product_image_key,
      unit_label: it.product_unit_label,
      qty: it.qty,
      unit_price: it.unit_price,
      line_total: it.line_total,
    })),
    address: {
      full_name: address.label,
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      state: address.state,
      pin: address.pin,
      phone: address.phone,
    },
    payment_method: payload.payment_method || "cod",
    payment_status: payload.payment_method === "cod" ? "pending" : "paid",
    delivery_method,
    notes: payload.notes || null,
    coupon_code: cartState.coupon?.code || null,
    subtotal: cartState.subtotal,
    gst_amount: cartState.gst_amount,
    shipping,
    discount: cartState.discount,
    total,
    cancelled_at: null,
    courier: {
      name: "Delhivery",
      awb: "AWB" + Math.floor(1000000 + Math.random() * 8999999),
      rider: "Sandeep K.",
      rider_phone: "+91 98674 32109",
    },
  };

  // Persist the order.
  const all = store.get("orders", []) || [];
  store.set("orders", [order, ...all]);

  // Empty the cart.
  await cart.clear();

  return withLatency(order);
}
